/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * Strict typed-DELETE confirmation modal. Self-contained: owns the
 * typed-confirm string + delete-in-flight flag. Calls back into the
 * orchestrator via `onDeleted` so the parent can refresh the list /
 * status banner. Backend call uses `?confirm=true` exactly like V1.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import { deleteBackup } from "./backupApi";

export default function BackupDeleteDialog({ target, onOpenChange, onDeleted }) {
  const [typedConfirm, setTypedConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Reset whenever the dialog opens against a new target.
  useEffect(() => {
    setTypedConfirm("");
  }, [target]);

  const confirm = async () => {
    if (!target) return;
    if (typedConfirm !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    setDeleting(true);
    try {
      await deleteBackup(target.backup_id);
      toast.success("Backup deleted");
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error("Delete failed");
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent data-testid="delete-backup-dialog">
        <DialogHeader>
          <DialogTitle>Delete backup</DialogTitle>
          <DialogDescription>
            This deletes the server-side ZIP and its metadata row. It does NOT affect live
            business data. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="text-xs font-mono">{target.filename}</div>
            <Label htmlFor="deleteTypedConfirm">Type DELETE to confirm</Label>
            <Input
              id="deleteTypedConfirm"
              data-testid="delete-typed-confirm-input"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder="DELETE"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={confirm}
            disabled={deleting || typedConfirm !== "DELETE"}
            data-testid="confirm-delete-btn"
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
